import type { CustomFile } from "core/entities";
import { useToaster } from "core/hooks/useToaster";
import { uuid } from "core/utils/uuid";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  PAYMENT_REQUEST_DOCUMENT_ACCEPTED_MIME_TYPES,
  type PaymentRequestDocumentType,
} from "../entities";

const defaultValues = {
  documentType: "",
  description: "",
  document: null,
};

export function useDocumentState(initialDocuments: CustomFile[] = []) {
  const [newDocuments, setNewDocuments] =
    useState<CustomFile[]>(initialDocuments);
  const { errorToast } = useToaster();
  const { t } = useTranslation("PaymentRequest");
  const formMethods = useForm({
    defaultValues,
  });

  const { getValues, reset, watch, setError } = formMethods;

  const document = watch("document");

  const handleDocumentChange = useCallback(
    (documentArgument: FileList) => {
      const [type, description] = getValues(["documentType", "description"]);
      const file = documentArgument?.[0] as CustomFile;

      if (!file || !type || typeof file.type !== "string") {
        return;
      }
      if (
        !PAYMENT_REQUEST_DOCUMENT_ACCEPTED_MIME_TYPES.includes(file.type as any)
      ) {
        const errorMessage = t(
          "Next:PaymentRequest:UseDocumentState.Error.FileTypeNotSupported"
        );
        errorToast(errorMessage);
        setError("document", { message: errorMessage });
        reset(defaultValues);
        return;
      }

      if (newDocuments.some((newDoc) => newDoc.name === file.name)) {
        const errorMessage = t(
          "Next:PaymentRequest:UseDocumentState.Error.FileAlreadyExists"
        );
        errorToast(errorMessage);
        setError("document", { message: errorMessage });
        reset(defaultValues);
        return;
      }

      file.id = uuid();
      file.documentType = type as PaymentRequestDocumentType;
      file.description = description;
      setNewDocuments((docs) => {
        const newDocsArray = [...docs, file];
        return newDocsArray;
      });

      reset(defaultValues);
    },
    [getValues, newDocuments, reset, t, errorToast, setError]
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handleDocumentChange(document!);
  }, [document, handleDocumentChange]);

  function newDocumentsCancelClick({ index }: { index: number }) {
    setNewDocuments((docs) => [
      ...docs.slice(0, index),
      ...docs.slice(index + 1),
    ]);
  }

  function clearNewDocuments() {
    setNewDocuments([]);
  }

  return {
    documentIdList: newDocuments
      .map((doc) => doc?.id)
      .filter((id): id is string => typeof id === "string"),
    newDocuments,
    newDocumentsCancelClick,
    clearNewDocuments,
    setError,
    formMethods,
  };
}
